import { useState } from "react";
import { createTask, useTasks } from "../../lib/queries";
import { useCockpit } from "../../lib/state";
import type { ProjectTask, TaskStatus } from "../../types/database";
import { useQueryClient } from "@tanstack/react-query";

interface TasksPaneProps {
  projectName: string;
}

const COLUMNS: Array<{ status: TaskStatus; label: string; color: string }> = [
  { status: "todo", label: "Todo", color: "var(--plum)" },
  { status: "in_progress", label: "In progress", color: "#FF1FA5" },
  { status: "blocked", label: "Blocked", color: "#cf6679" },
  { status: "done", label: "Done", color: "#7ec699" },
];

export default function TasksPane({ projectName }: TasksPaneProps) {
  const { data: tasks = [] } = useTasks(projectName);
  const openDrawer = useCockpit((s) => s.openDrawer);
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");

  const grouped: Record<TaskStatus, ProjectTask[]> = {
    todo: [],
    in_progress: [],
    blocked: [],
    done: [],
  };
  for (const t of tasks) grouped[t.status].push(t);

  const submit = async () => {
    const title = draft.trim();
    if (!title) {
      setAdding(false);
      setDraft("");
      return;
    }
    await createTask({ project: projectName, title });
    setDraft("");
    setAdding(false);
    qc.invalidateQueries({ queryKey: ["project_tasks"] });
  };

  return (
    <div className="aero-glass mx-3 mt-3 flex flex-col" style={{ borderRadius: 22 }}>
      <div
        className="flex items-center justify-between px-4 py-3"
        style={{ borderBottom: "1px solid rgba(255,31,165,0.15)" }}
      >
        <div className="flex items-center gap-3">
          <span
            className="text-lg"
            style={{
              fontFamily: "Fraunces, serif",
              fontStyle: "italic",
              fontWeight: 700,
              color: "var(--ink)",
            }}
          >
            <span className="holo-text">Tasks</span>
          </span>
          <span
            className="font-eyebrow"
            style={{
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: "0.18em",
              color: "var(--plum)",
              opacity: 0.6,
            }}
          >
            · {tasks.length}
          </span>
        </div>
        <button
          type="button"
          data-tasks-new
          onClick={() => setAdding(true)}
          className="glossy-pill glossy-pill--small"
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 5v14M5 12h14" />
          </svg>
          New
          <span className="kbd">⌘</span>
          <span className="kbd">N</span>
        </button>
      </div>
      {adding ? (
        <div className="px-4 py-3" style={{ borderBottom: "1px solid rgba(255,31,165,0.15)" }}>
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submit();
              if (e.key === "Escape") {
                setAdding(false);
                setDraft("");
              }
            }}
            onBlur={submit}
            placeholder="What's the move? (Enter to save)"
            className="w-full px-3 py-2 outline-none"
            style={{
              background: "rgba(255,255,255,0.6)",
              border: "1px solid rgba(255,255,255,0.7)",
              borderRadius: 14,
              fontFamily: "Fraunces, serif",
              fontStyle: "italic",
              fontSize: 14,
              color: "var(--ink)",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,.85)",
            }}
          />
        </div>
      ) : null}
      <div className="grid grid-cols-4 gap-2 p-3">
        {COLUMNS.map((col) => (
          <div
            key={col.status}
            className="aero-glass aero-glass-tight flex flex-col px-2 py-2"
            style={{ borderRadius: 16 }}
          >
            <div className="flex items-center justify-between px-2 py-1.5">
              <span
                className="font-eyebrow"
                style={{
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: col.color,
                }}
              >
                {col.label}
              </span>
              <span
                className="font-eyebrow"
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "var(--plum)",
                  opacity: 0.55,
                }}
              >
                {grouped[col.status].length}
              </span>
            </div>
            <div className="flex flex-col gap-1.5">
              {grouped[col.status].map((t) => (
                <button
                  type="button"
                  key={t.id}
                  onClick={() => openDrawer("task", t.id)}
                  className="aero-glass aero-glass-tight px-3 py-2 text-left transition-transform hover:-translate-y-0.5"
                  style={{ borderRadius: 12 }}
                >
                  <div
                    className="leading-snug"
                    style={{
                      fontFamily: "Manrope, sans-serif",
                      fontWeight: 600,
                      fontSize: 12.5,
                      color: "var(--ink)",
                    }}
                  >
                    {t.title}
                  </div>
                  <div className="mt-1.5 flex items-center gap-1.5">
                    <span
                      className="glossy-project-pill"
                      style={{
                        background: "linear-gradient(135deg, #c7a8ff, #9c7fff)",
                        fontSize: 8,
                        padding: "2px 7px",
                      }}
                    >
                      {t.kind}
                    </span>
                    {t.agent_owner ? (
                      <span
                        className="font-eyebrow"
                        style={{
                          fontSize: 9,
                          fontWeight: 700,
                          letterSpacing: "0.1em",
                          color: "var(--hot-pink)",
                        }}
                      >
                        {t.agent_owner}
                      </span>
                    ) : null}
                  </div>
                </button>
              ))}
              {grouped[col.status].length === 0 ? (
                <span
                  className="px-2 py-3 text-xs"
                  style={{
                    fontFamily: "Fraunces, serif",
                    fontStyle: "italic",
                    color: "var(--plum)",
                    opacity: 0.4,
                  }}
                >
                  empty, queen
                </span>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
